/**
 * Seed Data - Test data for volunteer training
 */

import { saveRoster, saveRace, setSelectedRace } from './storage.js'

// First names by gender
const MALE_NAMES = [
  'James', 'John', 'Robert', 'Michael', 'William', 'David', 'Joseph', 'Thomas', 'Charles', 'Daniel',
  'Matthew', 'Anthony', 'Mark', 'Steven', 'Paul', 'Andrew', 'Joshua', 'Kenneth', 'Kevin', 'Brian',
  'George', 'Timothy', 'Ronald', 'Edward', 'Jason', 'Jeffrey', 'Ryan', 'Jacob', 'Gary', 'Nicholas',
  'Eric', 'Jonathan', 'Stephen', 'Larry', 'Justin', 'Scott', 'Brandon', 'Benjamin', 'Samuel', 'Raymond',
  'Gregory', 'Frank', 'Alexander', 'Patrick', 'Jack', 'Dennis', 'Jerry', 'Tyler', 'Aaron', 'Jose'
]

const FEMALE_NAMES = [
  'Mary', 'Patricia', 'Jennifer', 'Linda', 'Barbara', 'Elizabeth', 'Susan', 'Jessica', 'Sarah', 'Karen',
  'Lisa', 'Nancy', 'Betty', 'Margaret', 'Sandra', 'Ashley', 'Kimberly', 'Emily', 'Donna', 'Michelle',
  'Dorothy', 'Carol', 'Amanda', 'Melissa', 'Deborah', 'Stephanie', 'Rebecca', 'Sharon', 'Laura', 'Cynthia',
  'Kathleen', 'Amy', 'Angela', 'Shirley', 'Anna', 'Brenda', 'Pamela', 'Emma', 'Nicole', 'Helen',
  'Samantha', 'Katherine', 'Christine', 'Debra', 'Rachel', 'Carolyn', 'Janet', 'Catherine', 'Maria', 'Heather'
]

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
  'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
  'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson',
  'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
  'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell', 'Carter', 'Roberts'
]

const TEAMS = [
  'Mana Running Club', 'Pacific Striders', 'Bay Area Track', 'Golden Gate Runners',
  'Silicon Valley TC', 'East Bay Flyers', 'Peninsula Pacers', 'Sunset Runners',
  'Ocean Beach RC', 'Mission District TC'
]

/**
 * Generate random integer between min and max (inclusive)
 */
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

/**
 * Pick random item from array
 */
function randomPick(arr) {
  return arr[Math.floor(Math.random() * arr.length)]
}

/**
 * Generate test roster of 100 runners
 */
export function generateTestRoster() {
  const athletes = []

  for (let i = 0; i < 100; i++) {
    const bib = 101 + i
    const gender = i < 50 ? 'M' : 'F'  // 50 male, 50 female
    const firstName = gender === 'M' ? randomPick(MALE_NAMES) : randomPick(FEMALE_NAMES)
    const lastName = randomPick(LAST_NAMES)

    // Distribute ages: roughly 20% per age group
    let age
    const ageRoll = randomInt(1, 100)
    if (ageRoll <= 15) {
      age = randomInt(14, 19)  // 14-19
    } else if (ageRoll <= 35) {
      age = randomInt(20, 29)  // 20-29
    } else if (ageRoll <= 55) {
      age = randomInt(30, 39)  // 30-39
    } else if (ageRoll <= 75) {
      age = randomInt(40, 49)  // 40-49
    } else if (ageRoll <= 90) {
      age = randomInt(50, 59)  // 50-59
    } else {
      age = randomInt(60, 75)  // 60+
    }

    athletes.push({
      bib,
      firstName,
      lastName,
      gender,
      age,
      ageGroup: null, // Will be calculated from race preset
      grade: null,
      division: null,
      team: randomPick(TEAMS)
    })
  }

  return athletes
}

/**
 * Create test race and roster
 */
export function seedTestData() {
  // Create the Superbowl 5k race
  const race = {
    name: 'Superbowl 5k',
    date: new Date().toISOString().split('T')[0],
    distanceValue: 5,
    distanceUnit: 'kilometers',
    ageGroupPreset: 'standard',
    raceType: 'road',
    status: 'ready'
  }

  // Save the race and get the ID back
  const savedRace = saveRace(race)

  // Generate and save roster for this race
  const athletes = generateTestRoster()
  saveRoster(athletes, savedRace.id)

  // Select this race
  setSelectedRace(savedRace.id)

  console.log('Test data seeded:', {
    race: savedRace.name,
    raceId: savedRace.id,
    athletes: athletes.length
  })

  return { race: savedRace, athletes }
}

/**
 * Check URL for seed parameter and auto-seed if present
 */
export function checkAutoSeed() {
  const params = new URLSearchParams(window.location.search)
  if (params.get('seed') === 'test') {
    const result = seedTestData()
    // Remove the parameter from URL
    window.history.replaceState({}, '', window.location.pathname + window.location.hash)
    return result
  }
  return null
}

/**
 * Parse CSV text into array of objects
 */
function parseCSV(csvText) {
  const lines = csvText.trim().split('\n')
  const headers = lines[0].split(',').map(h => h.trim())
  const athletes = []

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',')
    if (values.length < headers.length) continue

    const obj = {}
    headers.forEach((header, idx) => {
      obj[header] = values[idx]?.trim() || ''
    })

    // Map to our athlete format
    athletes.push({
      bib: parseInt(obj['Bib'], 10) || 0,
      firstName: obj['First Name'] || '',
      lastName: obj['Last Name'] || '',
      gender: obj['Gender'] || '',
      age: parseInt(obj['Age'], 10) || null,
      ageGroup: null,
      grade: null,
      division: null,
      team: null
    })
  }

  return athletes
}

/**
 * Seed Foot Forward races with real roster data
 * Call this from browser console: seedFootForwardRaces()
 */
export async function seedFootForwardRaces() {
  // 1 Mile Race roster data (embedded)
  const oneMileCSV = `Bib,First Name,Last Name,Gender,Age
37,Kanako,Fujikawa,F,48
36,Chisako,Fujikawa,F,11
18,Raj,Chitambar,M,15
69,J.R.,Mintz,M,59
92,Anjali,Santhosh,F,24
91,Srivatsa,Sanketi,M,11
90,Sriniket,Sanketi,M,8
75,Aadya,Nayak,F,9
74,Viaan,Nawathe,M,7
24,Siddhiksha,Das,F,9`

  // 5K Race roster data (embedded - first batch)
  const fiveKCSV = `Bib,First Name,Last Name,Gender,Age
73,Jaya,Nair,F,37
15,Louis,Cervantes,M,31
110,Mustapha,Ward,M,37
64,Mayra,Manjarrez,F,36
13,Tania,Castro,F,34
2,Charlie,Amsden,X,31
32,Emma,Elliott,F,21
7,Joanne,Bates,F,43
8,Simona,Bhaya,F,13
52,Keri,King,F,48
30,Marquita,Druker,F,50
61,Oscar Andres,Lopez,M,15
60,Oscar,Lopez,M,56
55,Harish,Kunta,M,29
87,Keerthika,Ravikanti,F,30
9,Maneesh,Boddu,M,29
51,Sruthi,Kesa,F,24
4,Kimberly,Barentsen,F,60
5,Robert,Barentsen,M,59
81,Navin,Prabagaran,M,12
82,Ramesh,Prabagaran,M,47
65,Suma,Manvi,F,49
84,Savitha,Ramesh,F,48
88,Griselda,Rocha,F,56
79,Ayla,Patel,F,11
45,Gabriela,Hopkins-Moore,F,35
107,Taylor,Wakefield,F,32
77,Ishaan,Parmar,M,16
58,Camille,Leung,F,52
89,Marvin,Rombaua,M,41
14,Katheryne,Castro Rivera,F,30
40,Matthew,Golden,M,13
94,Janson,Serrin,F,13
26,Prisha,Dhruva,F,11
25,Kinjal,Dhruva,M,45
44,Joan,Holland,F,58
62,Teresa,Lorenzen,F,68
80,Jessica,Perez,F,27
93,Yvonne,Schwab,F,49
43,Caitlin,Hoffman,F,35
56,Joshua,Lam,M,16
21,Pat,Clark,M,54
20,Fionn,Clark,M,16
19,Declan,Clark,M,16
101,Shelley,Snowdon,F,57
46,Shotaro,Horibe,M,14
67,Lily,Mori,F,12
66,Haruki,Mori,M,14
68,Maki,Mori,F,45
100,Vidhya,Shanker,F,44
96,Akshay,Sharma,M,32
95,Ritika,Sharma,F,32
39,Ella,Gee,F,8
38,Ben,Gee,M,52
85,Ryan,Richards,M,29
86,Sarah,Richards,F,28
3,Emily,Arnow,F,31
17,Gavin,Chew,M,8
16,Ginger,Chew,F,42
105,Dayana,Torres,F,49
104,Alisson,Torres,F,23
103,Victor,Tiznado,M,52
108,Yinyan,Wang,F,38
112,Jocelyn,Wong,F,10
111,Esther,Wong,F,44
1,Ravi,Alla,M,47
97,Emma,Shivers,F,24
78,Sanjana,Pasupuleti,F,15
109,Jordan,Willis,F,34
54,Bhagyashri,Kulkarni,F,42
70,Katherine,Moore,F,48
41,Samantha,Gonzalez,F,15
48,Maya,Inafuku,F,16
47,Kenji,Inafuku,M,49
42,John,Hannan,M,60
12,Sebastian,Carter,M,10
11,Ryan,Carter,M,44
10,Brenda,Carter,F,45
57,Maya,Le,F,47
35,Ethan,Frankle,M,8
34,Sarah,Frankle,F,40
33,Jeremy,Frankle,M,41
23,Brian,Conley,M,56
22,Debi,Conley,F,53
99,Dhruv,Shrivastava,M,13
98,Sachin,Shrivastava,M,48
31,David,Elliot,M,52
102,Dylan,Spencer-Salmon,M,14
83,Jessica,Ramirez,F,24
50,Vidya,Kapur,F,43
49,Zara,Kapur,F,10
6,Greg,Barton,M,67
76,Pavan,Patel,M,44
29,Nina,Doshi,F,41
28,Samir,Doshi,M,44
27,Jai,Doshi,M,11
72,Parag,Mujumdar,M,42
71,Priti,Mujumdar,F,42
53,Aaron,Kramer,M,56
63,Sofia,Manfredi,F,40
106,Eric,Tsai,M,36
59,Emily,Martin,F,30`

  // Create 1 Mile race
  const oneMileRace = saveRace({
    name: 'Foot Forward 1 Mile Run',
    date: '2026-02-01',
    distanceValue: 1,
    distanceUnit: 'miles',
    ageGroupPreset: 'none',
    raceType: 'road',
    status: 'ready'
  })

  // Create 5K race
  const fiveKRace = saveRace({
    name: 'Foot Forward 5K',
    date: '2026-02-01',
    distanceValue: 5,
    distanceUnit: 'kilometers',
    ageGroupPreset: 'foot_forward',
    raceType: 'road',
    status: 'ready'
  })

  // Parse and save rosters
  const oneMileAthletes = parseCSV(oneMileCSV)
  const fiveKAthletes = parseCSV(fiveKCSV)

  saveRoster(oneMileAthletes, oneMileRace.id)
  saveRoster(fiveKAthletes, fiveKRace.id)

  // Select the 5K race by default
  setSelectedRace(fiveKRace.id)

  console.log('Foot Forward races created:', {
    oneMile: { id: oneMileRace.id, athletes: oneMileAthletes.length },
    fiveK: { id: fiveKRace.id, athletes: fiveKAthletes.length }
  })

  return {
    oneMileRace,
    fiveKRace,
    oneMileAthletes,
    fiveKAthletes
  }
}
